import type { Patrol } from '@local/common-lib';
import {
  ArrowBack as CancelIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { Theme } from '@mui/material';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FC } from 'react';
import type { Contract } from 'validata';
import { isBoolean, isString } from 'validata';
import { FormikSwitch } from '../../components/formik-fields/switch.js';
import { FormikTextField } from '../../components/formik-fields/text-field.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import type { EditViewProps } from '../../utils/types.js';

interface Props extends EditViewProps<EccLogOnForm> {
  isImmediate?: boolean;
  onRecentVehicleClick: (vehicle: Patrol.PatrolVehicleInfo) => void;
  onRecentVehicleDelete: (vehicle: Patrol.PatrolVehicleInfo) => void;
  recentVehicles: Patrol.PatrolVehicleInfo[];
}

export interface EccLogOnForm {
  hasPoliceRadio: boolean;
  vehicle: boolean;
  vehicleColor: string;
  vehicleHasLivery: boolean;
  vehicleMake: string;
  vehicleModel: string;
  vehicleRego: string;
}

export const eccLogOnFormContractNoVehicle: Contract<
  Omit<
    EccLogOnForm,
    | 'vehicle'
    | 'vehicleColor'
    | 'vehicleHasLivery'
    | 'vehicleMake'
    | 'vehicleModel'
    | 'vehicleRego'
  >
> = {
  hasPoliceRadio: isBoolean(),
};

export const eccLogOnFormContract: Contract<Omit<EccLogOnForm, 'vehicle'>> = {
  ...eccLogOnFormContractNoVehicle,
  vehicleColor: isString({ minLength: 1, trim: 'both' }),
  vehicleHasLivery: isBoolean(),
  vehicleMake: isString({ minLength: 1, trim: 'both' }),
  vehicleModel: isString({ minLength: 1, trim: 'both' }),
  vehicleRego: isString({
    maxLength: 6,
    minLength: 1,
    regex: /^[0-9a-zA-Z]+$/,
    transform: (value) => value.toUpperCase(),
    trim: 'both',
  }),
};

export const EccLogOnView: FC<Props> = ({
  cancel,
  formik,
  isImmediate = false,
  onRecentVehicleClick,
  onRecentVehicleDelete,
  recentVehicles,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  // view
  return (
    <form onReset={formik.handleReset} onSubmit={formik.handleSubmit}>
      <CenterLayout>
        <Stack direction="column" spacing={3}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant={smallScreen ? 'h6' : 'h4'}>
                ECC Logon
              </Typography>
              <Typography color="text.secondary" variant="subtitle2">
                Send Logon email to NZ Police Emergency Communications Centre
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardHeader
              action={<FormikSwitch formik={formik} id="vehicle" label="" />}
            />
            <CardContent>
              <Collapse in={!formik.values.vehicle}>
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  A vehicle is not being used for this patrol.
                </Typography>
              </Collapse>
              <Collapse in={formik.values.vehicle}>
                <Stack direction="column" spacing={0}>
                  {recentVehicles.length ? (
                    <Stack direction="column" spacing={2}>
                      <Typography
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                        variant="body2"
                      >
                        Select previous vehicle or enter details below
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ overflowX: 'auto', pb: 2 }}
                      >
                        {recentVehicles.map((vehicle) => (
                          <Chip
                            key={vehicle.rego}
                            label={vehicle.rego}
                            onClick={() => onRecentVehicleClick(vehicle)}
                            onDelete={() => onRecentVehicleDelete(vehicle)}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : undefined}
                  <Stack direction="column" spacing={3}>
                    <Stack direction={{ md: 'row', xs: 'column' }} spacing={3}>
                      <FormikTextField
                        formik={formik}
                        id="vehicleRego"
                        inputProps={{
                          sx: { textTransform: 'uppercase' },
                        }}
                        label="Rego"
                      />
                      <FormikTextField
                        formik={formik}
                        id="vehicleColor"
                        inputProps={{
                          sx: { textTransform: 'capitalize' },
                        }}
                        label="Color"
                      />
                    </Stack>
                    <Stack direction={{ md: 'row', xs: 'column' }} spacing={3}>
                      <FormikTextField
                        formik={formik}
                        id="vehicleMake"
                        inputProps={{
                          sx: { textTransform: 'capitalize' },
                        }}
                        label="Make"
                      />
                      <FormikTextField
                        formik={formik}
                        id="vehicleModel"
                        inputProps={{
                          sx: { textTransform: 'capitalize' },
                        }}
                        label="Model"
                      />
                    </Stack>
                    <FormikSwitch
                      formik={formik}
                      id="vehicleHasLivery"
                      label="Vehicle has livery"
                    />
                  </Stack>
                </Stack>
              </Collapse>
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Details" />
            <CardContent>
              <FormikSwitch
                formik={formik}
                id="hasPoliceRadio"
                label="Patrollers will have a Police radio"
              />
            </CardContent>
            <CardContent>
              <Stack direction="column" spacing={2}>
                <Typography>
                  On pressing the "Send Logon Now" button below, an email will
                  be sent to NZ Police Emergency Communications Centre (ECC) on
                  your behalf. The email will be pre-filled with the details of
                  this patrol.
                </Typography>
                <Typography>
                  The reply from ECC will be to <b>your email</b> and will
                  include an Police Event Number that should be referenced in
                  any further communications, as well as recorded in the form
                  when you start the patrol.
                </Typography>
              </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', pb: 3 }}>
              <LoadingButton
                color="primary"
                fullWidth
                loading={formik.isSubmitting}
                onClick={cancel}
                startIcon={<CancelIcon />}
              >
                {isImmediate ? 'Do ECC Login Later' : 'Cancel'}
              </LoadingButton>
              <LoadingButton
                color="primary"
                fullWidth
                loading={formik.isSubmitting}
                startIcon={<EmailIcon />}
                type="submit"
                variant="contained"
              >
                Send Logon Now
              </LoadingButton>
            </CardActions>
          </Card>
        </Stack>
      </CenterLayout>
    </form>
  );
};
